import {
  createActionsColumn,
  WbTable,
  WbTabsContent,
} from '@agilelab/plugin-wb-platform';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@mainstage/catalog-model';
import { useRouteRef } from '@mainstage/core-plugin-api';
import { catalogLocationCreatePermission } from '@mainstage/plugin-catalog-common/alpha';
import { usePermission } from '@mainstage/plugin-permission-react';
import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { selectedTemplateRouteRef } from '../../routes';
import { useTechDocsDrawer } from '../hooks/useTechDocsDrawer';
import { useScaffolderPageContext } from './context/useScaffolderPageContext';
import { useWitboostCustomTemplates } from './utils';
import {
  ActionColumn,
  DescriptionColumn,
  ErrorColumn,
  createNameColumnRenderer,
  TagsColumn,
  TypeColumn,
} from './columns';

export const ScaffolderTable: FC = () => {
  const navigate = useNavigate();
  const { entities, loading } = useScaffolderPageContext();
  const witboostCustomTemplates = useWitboostCustomTemplates(entities);
  const { openDocs, TechDocsDrawer } = useTechDocsDrawer();
  const location = useLocation();
  const knownSystem = location.state?.system;
  const knownDomain = location.state?.domain;

  const templateRoute = useRouteRef(selectedTemplateRouteRef);

  const getEntityLink = (entity: Entity) => {
    const { name, namespace } = parseEntityRef(stringifyEntityRef(entity));
    const href = templateRoute({ templateName: name, namespace });

    const to =
      knownDomain && knownSystem
        ? `${href}?formData=%7B"domain"%3A"${knownDomain}"%2C"dataproduct"%3A"system:${knownSystem}"%7D`
        : href;

    return to;
  };

  const { allowed } = usePermission({
    permission: catalogLocationCreatePermission,
  });

  return (
    <WbTabsContent value="table">
      <WbTable<any>
        pinActionsColumnOnMobile={false}
        components={{
          tableLoader: { loading },
          tableContent: {
            columns: [
              {
                field: 'metadata.title',
                headerName: 'Name',
                width: 300,
                fieldRender: entity => {
                  const to = getEntityLink(entity);
                  return createNameColumnRenderer({ allowed, to })(entity);
                },
                sortable: true,
              },
              {
                field: 'metadata.classDetails.displayName',
                headerName: 'Type',
                fieldRender: TypeColumn,
                sortable: true,
                width: 150,
              },
              {
                field: 'metadata.description',
                headerName: 'Description',
                fieldRender: DescriptionColumn,
                sortable: true,
              },
              {
                field: 'metadata.tags',
                headerName: 'Tags',
                fieldRender: TagsColumn,
              },
              createActionsColumn({
                width: 110,
                renderActions: e => (
                  <div
                    style={{
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <ErrorColumn
                      entity={e}
                      witboostCustomTemplates={witboostCustomTemplates}
                    />
                    <ActionColumn
                      entity={e}
                      witboostCustomTemplates={witboostCustomTemplates}
                      onOpenDocs={openDocs}
                    />
                  </div>
                ),
              }),
            ],
            rows: entities,
            onRowClick: (entity, e) => {
              e.stopPropagation();
              e.preventDefault();
              if (allowed) {
                const to = getEntityLink(entity);

                navigate(to);
              }
            },
          },
        }}
      />
      {TechDocsDrawer}
    </WbTabsContent>
  );
};
